import React, { useState } from "react";
import { motion } from "framer-motion";
import { Github, Linkedin, Mail, Cloud, Shield, Activity, ArrowRight, GitBranch, Database, Zap, CheckCircle, Award } from "lucide-react";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

export default function Portfolio() {
  const [dark, setDark] = useState(true);
  const bg = dark ? "bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-slate-100" : "bg-gradient-to-br from-white via-slate-50 to-blue-50 text-slate-900";
  const card = dark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-800 border border-slate-200";
  const text = dark ? "text-slate-400" : "text-slate-600";
  const heading = dark ? "text-slate-100" : "text-slate-900";
  
  return (
    <div className={`${bg} min-h-screen transition-colors duration-500 relative overflow-hidden`}>
      {/* Enhanced Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_1px_1px,rgba(148,163,184,0.15)_1px,transparent_0)] bg-[length:40px_40px]" />
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-sky-500/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute top-1/3 -right-32 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />
        <motion.div className="absolute bottom-0 left-1/2 w-full h-px bg-gradient-to-r from-transparent via-sky-500/50 to-transparent" animate={{ opacity: [0.3, 0.8, 0.3] }} transition={{ duration: 3, repeat: Infinity }} />
        
        {/* Side Floating Accents */}
        <motion.div 
          className="fixed left-8 top-1/3 w-2 h-2 bg-sky-400 rounded-full opacity-40"
          animate={{ y: [0, 40, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="fixed left-16 top-2/3 w-1.5 h-1.5 bg-blue-500 rounded-full opacity-30"
          animate={{ y: [0, -50, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="fixed right-8 top-1/4 w-1.5 h-1.5 bg-indigo-400 rounded-full opacity-30"
          animate={{ y: [0, -40, 0] }}
          transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div 
          className="fixed right-20 top-3/4 w-2 h-2 bg-sky-500 rounded-full opacity-40"
          animate={{ y: [0, 50, 0] }}
          transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>
      
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 py-24 relative">
        <button
          onClick={() => setDark(!dark)}
          className={`${dark ? "bg-slate-800 text-slate-200 hover:bg-slate-700" : "bg-slate-200 text-slate-900 hover:bg-slate-300"} absolute top-6 right-6 px-4 py-2 rounded-xl transition`}
        >
          {dark ? "☀️ Light" : "🌙 Dark"}
        </button>
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-3 mb-4">
              <motion.div className="w-3 h-3 bg-sky-400 rounded-full" animate={{ scale: [1, 1.5, 1], opacity: [1, 0.5, 1] }} transition={{ duration: 2, repeat: Infinity }} />
              <span className="text-sky-400 font-semibold">Senior SRE & Cloud Engineer</span>
            </div>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="text-5xl md:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-sky-400 via-blue-500 to-indigo-500">
              Akhil Movva
            </motion.h1>
          </motion.div>
          <motion.div 
            initial={{ opacity: 0, scale: 0.8, x: 50 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative hidden md:flex justify-center"
          >
            <div className="relative w-80 h-80">
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-sky-500 to-indigo-500 rounded-full blur-3xl opacity-30"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 3, repeat: Infinity }}
              />
              <img 
                src="/profile.jpg" 
                alt="Akhil Movva"
                className={`relative w-full h-full object-cover rounded-full shadow-2xl filter ${dark ? 'brightness-75' : 'brightness-110'} contrast-110`}
              />
            </div>
          </motion.div>
        </div>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} className="mt-8">
          <p className={`text-xl ${heading}`}>Senior Site Reliability Engineer | Cloud Engineer</p>
          <p className={`mt-6 max-w-2xl ${text}`}>
            Designing reliable, secure, and scalable cloud platforms across GCP, AWS, and Kubernetes —
            with a strong focus on automation, observability, and healthcare compliance.
          </p>
          <div className="mt-8 flex gap-4">
            <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="https://www.linkedin.com/in/akhilmovva" className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-500 transition">LinkedIn</motion.a>
            <motion.a whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} href="https://github.com/akhilmovva" className={`px-5 py-3 rounded-2xl ${dark ? "bg-slate-800 hover:bg-slate-700" : "bg-slate-200 hover:bg-slate-300 text-slate-900"} transition`}>GitHub</motion.a>
          </div>
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Platform Pipeline */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className={`text-3xl font-semibold mb-10 ${heading}`}>The SRE Platform Stack</motion.h2>
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" className="flex flex-col md:flex-row items-center justify-between gap-4">
          <motion.div variants={itemVariants}><PipelineStage icon={<GitBranch />} label="CI/CD" desc="GitLab, GitHub Actions" dark={dark} /></motion.div>
          <motion.div variants={itemVariants} className="hidden md:block"><ArrowRight className="w-8 h-8 text-sky-500" /></motion.div>
          <motion.div variants={itemVariants}><PipelineStage icon={<Database />} label="Infrastructure" desc="Terraform, Kubernetes" dark={dark} /></motion.div>
          <motion.div variants={itemVariants} className="hidden md:block"><ArrowRight className="w-8 h-8 text-sky-500" /></motion.div>
          <motion.div variants={itemVariants}><PipelineStage icon={<Zap />} label="Observability" desc="Prometheus, Grafana" dark={dark} /></motion.div>
          <motion.div variants={itemVariants} className="hidden md:block"><ArrowRight className="w-8 h-8 text-sky-500" /></motion.div>
          <motion.div variants={itemVariants}><PipelineStage icon={<Shield />} label="Reliability" desc="SLOs, Runbooks" dark={dark} /></motion.div>
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Projects */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className={`text-3xl font-semibold mb-4 ${heading}`}>Highlighted Work</motion.h2>
        <p className={`${text} mb-10 max-w-3xl`}>Architecture-focused work highlighting how I design, operate, and scale reliable cloud platforms.</p>
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" className="grid md:grid-cols-3 gap-6">
          <motion.div variants={itemVariants}>
            <Project 
              title="Cloud-Native PACS Platform" 
              desc="Designed GCP infrastructure with Terraform, Kubernetes, and HIPAA-compliant observability."
              tech={["GCP", "Terraform", "K8s", "Observability"]}
              dark={dark}
              icon="☁️"
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <Project 
              title="Kubernetes CI/CD Platform" 
              desc="GitLab pipelines, Helm deployments, blue-green rollouts, and GitOps workflows."
              tech={["GitLab", "Helm", "GitOps", "ArgoCD"]}
              dark={dark}
              icon="🚀"
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <Project 
              title="Observability & Reliability Stack" 
              desc="Prometheus, Grafana dashboards, alerting, and incident recovery automation."
              tech={["Prometheus", "Grafana", "Alerting", "Automation"]}
              dark={dark}
              icon="📊"
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Skills with Proficiency */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className={`text-3xl font-semibold mb-10 ${heading}`}>Technical Expertise</motion.h2>
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" className="grid md:grid-cols-3 gap-6">
          <motion.div variants={itemVariants}><SkillCard icon={<Cloud />} title="Cloud Platforms" items={["AWS", "Google Cloud", "Azure"]} proficiency={95} dark={dark} /></motion.div>
          <motion.div variants={itemVariants}><SkillCard icon={<Shield />} title="DevOps & IaC" items={["Terraform", "Kubernetes", "Docker", "Helm", "CI/CD"]} proficiency={98} dark={dark} /></motion.div>
          <motion.div variants={itemVariants}><SkillCard icon={<Activity />} title="Observability & SRE" items={["Grafana", "Prometheus", "Splunk", "ELK", "Incident Response"]} proficiency={96} dark={dark} /></motion.div>
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Experience */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className={`text-3xl font-semibold mb-6 ${heading}`}>How I Think as an SRE</motion.h2>
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" className="grid md:grid-cols-3 gap-6 mb-16">
          <motion.div variants={itemVariants}>
            <motion.div whileHover={{ scale: 1.05 }} className={`p-6 rounded-2xl shadow-xl backdrop-blur ${card}`}>
              <div className="text-3xl mb-3">🎯</div>
              <h3 className="text-xl font-semibold mb-2">Reliability First</h3>
              <p className={text}>I design systems around SLOs, error budgets, and resilience. Reliability is a feature, not an afterthought.</p>
            </motion.div>
          </motion.div>
          <motion.div variants={itemVariants}>
            <motion.div whileHover={{ scale: 1.05 }} className={`p-6 rounded-2xl shadow-xl backdrop-blur ${card}`}>
              <div className="text-3xl mb-3">📈</div>
              <h3 className="text-xl font-semibold mb-2">Observability Driven</h3>
              <p className={text}>Metrics, logs, and traces guide decisions. If it's not observable, it's not production-ready.</p>
            </motion.div>
          </motion.div>
          <motion.div variants={itemVariants}>
            <motion.div whileHover={{ scale: 1.05 }} className={`p-6 rounded-2xl shadow-xl backdrop-blur ${card}`}>
              <div className="text-3xl mb-3">⚙️</div>
              <h3 className="text-xl font-semibold mb-2">Automate Everything</h3>
              <p className={text}>From infrastructure to incident response, automation removes toil and enables scale.</p>
            </motion.div>
          </motion.div>
        </motion.div>

        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className={`text-3xl font-semibold mb-10 ${heading}`}>Experience Timeline</motion.h2>
        <motion.div initial="hidden" whileInView="visible" variants={containerVariants}>
          <Experience
            company="Optum"
            role="Senior Site Reliability Engineer"
            period="2024 – Present"
            points={[
              "Architecting scalable, secure cloud-native platforms using GCP and Kubernetes with focus on SRE best practices",
              "Designing and implementing comprehensive monitoring and alerting systems using Prometheus and Grafana for early issue detection",
              "Implementing disaster recovery and business continuity plans using Kubernetes failover and redundancy strategies",
              "Ensuring efficient incident recovery utilizing operational procedures, tools, and cross-functional stakeholder communication",
              "Managing and troubleshooting production environments with automation tools like Rundeck for orchestration",
              "Ensuring GCP infrastructure and applications maintain HIPAA compliance and security standards",
              "Collaborating with cross-functional teams to identify infrastructure issues and continuously improve system reliability"
            ]}
            dark={dark}
            icon="⭐"
            index={0}
          />
          <Experience
            company="Change Healthcare"
            role="Cloud Engineer II"
            period="2022 – 2023"
            points={[
              "Designed and implemented GCP-based infrastructure for PACS services using Terraform for resource provisioning and management",
              "Automated infrastructure and operational tasks using PowerShell and Bash scripting",
              "Collaborated with developers to optimize applications for cloud architecture, performance, scalability, and reliability",
              "Implemented CI/CD pipelines using GitLab pipelines and Jenkins for continuous integration and deployment",
              "Designed and deployed containerized infrastructure using Kubernetes and Docker for orchestration",
              "Set up monitoring and alerting systems with Grafana to ensure infrastructure meets performance and uptime requirements",
              "Created and maintained automation workflows in Rundeck to automate runbooks and alert triggering",
              "Implemented effective cloud infrastructure monitoring and emergency alerting systems for production operations"
            ]}
            dark={dark}
            icon="🚀"
            index={1}
          />
          <Experience
            company="OpsRoute"
            role="Cloud Engineer"
            period="2021 – 2022"
            points={[
              "Containerized and deployed Java Spring Boot applications to AWS-hosted Kubernetes clusters with Prometheus monitoring",
              "Developed Ansible playbooks and roles for Apache HTTPD, Tomcat, and MySQL installation across multiple environments",
              "Built fully automated CI/CD pipelines using GitLab, Maven, SonarQube, Nexus, and Docker registries",
              "Implemented Istio service mesh for enhanced observability, traffic management, and security",
              "Infrastructure as Code with Terraform: created dev, stage, and production environments with custom modules",
              "Orchestrated blue/green deployments using Terraform and Route53 weighted recordsets for zero-downtime releases",
              "Managed Kubernetes manifests with Helm and monitoring systems: Prometheus, Grafana, and Splunk"
            ]}
            dark={dark}
            icon="📦"
            index={2}
          />
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Certifications */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className={`text-3xl font-semibold mb-10 ${heading}`}>Certifications & Credentials</motion.h2>
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" className="grid md:grid-cols-3 gap-6">
          <motion.div variants={itemVariants}>
            <CertificationCard
              title="Google Digital Leader"
              icon="🔷"
              dark={dark}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <CertificationCard
              title="Terraform Associate"
              icon="📘"
              dark={dark}
            />
          </motion.div>
          <motion.div variants={itemVariants}>
            <CertificationCard
              title="AWS Certified Solutions Architect Associate"
              icon="🏆"
              dark={dark}
            />
          </motion.div>
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Education */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <motion.h2 initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className={`text-3xl font-semibold mb-10 ${heading}`}>Education</motion.h2>
        <motion.div initial="hidden" whileInView="visible" variants={containerVariants}>
          <Education
            university="Concordia University"
            degree="Master of Engineering (MEng)"
            field="Electrical and Computer Engineering"
            period="2019 – 2020"
            dark={dark}
            icon="🎓"
            index={0}
          />
          <Education
            university="National Institute of Technology Durgapur"
            degree="Bachelor of Technology (BTech)"
            field="Computer Science and Engineering"
            period="August 2014 – May 2018"
            dark={dark}
            icon="📚"
            index={1}
          />
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Contact Form Section */}
      <section className="max-w-2xl mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className={`p-10 rounded-3xl shadow-2xl backdrop-blur ${dark ? "bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-sky-500/20" : "bg-gradient-to-br from-white/80 to-sky-50/80 border border-sky-300/50"}`}
        >
          <motion.h2 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className={`text-3xl font-bold mb-2 text-center`}
          >
            Get in Touch
          </motion.h2>
          <p className={`text-center mb-8 ${text}`}>
            Send me a message and I'll respond as soon as possible.
          </p>
          <form action="https://formspree.io/f/xleaeonq" method="POST" className="space-y-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className={`w-full px-4 py-3 rounded-xl transition ${dark ? "bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:border-sky-500 focus:outline-none" : "bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none"}`}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className={`w-full px-4 py-3 rounded-xl transition ${dark ? "bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:border-sky-500 focus:outline-none" : "bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none"}`}
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <textarea
                name="message"
                rows="5"
                placeholder="Your Message"
                required
                className={`w-full px-4 py-3 rounded-xl transition resize-none ${dark ? "bg-slate-800 border border-slate-700 text-slate-100 placeholder-slate-500 focus:border-sky-500 focus:outline-none" : "bg-white border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:outline-none"}`}
              />
            </motion.div>
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(14, 165, 233, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="w-full px-8 py-4 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 font-semibold transition shadow-lg text-white"
            >
              Send Message
            </motion.button>
          </form>
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      {/* Get in Touch CTA */}
      <section className="max-w-4xl mx-auto px-6 py-20 text-center relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className={`p-12 rounded-3xl shadow-2xl backdrop-blur ${dark ? "bg-gradient-to-br from-slate-900/80 to-slate-800/80 border border-sky-500/20" : "bg-gradient-to-br from-white/80 to-sky-50/80 border border-sky-300/50"}`}
        >
          <motion.div
            className="absolute -top-12 left-1/2 transform -translate-x-1/2 text-5xl"
            animate={{ y: [0, -10, 0], rotate: [0, 5, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            💬
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            className={`text-4xl font-bold mb-4 mt-4 bg-clip-text text-transparent bg-gradient-to-r from-sky-400 to-blue-500`}
          >
            Ready to Build Something Great?
          </motion.h2>
          <p className={`text-lg mb-8 max-w-2xl mx-auto ${text}`}>
            I'm always interested in discussing cloud architecture, SRE best practices, and building reliable systems. Let's connect and explore how I can help your team.
          </p>
          <motion.div 
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.a 
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(14, 165, 233, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              href="mailto:akhilmovva6852@gmail.com" 
              className="px-8 py-4 rounded-2xl bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 font-semibold transition shadow-lg"
            >
              📧 Send an Email
            </motion.a>
            <motion.a 
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(14, 165, 233, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              href="https://www.linkedin.com/in/akhilmovva" 
              className={`px-8 py-4 rounded-2xl font-semibold transition shadow-lg ${dark ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-300 hover:bg-slate-400 text-slate-900"}`}
            >
              💼 Connect on LinkedIn
            </motion.a>
            <motion.a 
              whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(14, 165, 233, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              href="https://calendly.com" 
              className={`px-8 py-4 rounded-2xl font-semibold transition shadow-lg ${dark ? "bg-slate-700 hover:bg-slate-600" : "bg-slate-300 hover:bg-slate-400 text-slate-900"}`}
            >
              📅 Schedule a Call
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* Section Divider */}
      <motion.div initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} transition={{ duration: 1 }} className={`h-px mx-6 ${dark ? "bg-gradient-to-r from-transparent via-slate-700 to-transparent" : "bg-gradient-to-r from-transparent via-slate-300 to-transparent"}`} />

      <footer className={`${dark ? "border-slate-800" : "border-slate-300"} border-t py-10 text-center ${text}`}>
        <motion.div initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} className="flex justify-center gap-6 mb-4">
          <motion.a whileHover={{ scale: 1.2, color: "#0ea5e9" }} href="mailto:akhilmovva6852@gmail.com" className="hover:text-sky-400 transition"><Mail /></motion.a>
          <motion.a whileHover={{ scale: 1.2, color: "#0ea5e9" }} href="https://github.com/akhilmovva" className="hover:text-sky-400 transition"><Github /></motion.a>
          <motion.a whileHover={{ scale: 1.2, color: "#0ea5e9" }} href="https://www.linkedin.com/in/akhilmovva" className="hover:text-sky-400 transition"><Linkedin /></motion.a>
        </motion.div>
        © {new Date().getFullYear()} Akhil Movva
      </footer>
    </div>
  );
}

function PipelineStage({ icon, label, desc, dark }) {
  return (
    <motion.div whileHover={{ y: -4, boxShadow: "0 20px 40px rgba(14, 165, 233, 0.2)" }} className={`p-4 rounded-xl ${dark ? "bg-slate-900/50" : "bg-sky-50"} backdrop-blur text-center flex-1 border ${dark ? "border-slate-700/50" : "border-sky-200"}`}>
      <motion.div className="mb-2 text-sky-400 flex justify-center text-2xl" animate={{ y: [0, -5, 0] }} transition={{ duration: 2, repeat: Infinity }}>{icon}</motion.div>
      <h4 className="font-semibold mb-1">{label}</h4>
      <p className={`text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>{desc}</p>
    </motion.div>
  );
}

function SkillCard({ icon, title, items, proficiency, dark }) {
  const skillText = dark ? "text-slate-400" : "text-slate-600";
  return (
    <motion.div whileHover={{ y: -6, scale: 1.03 }} className={`p-6 rounded-2xl min-h-80 flex flex-col justify-between ${dark ? "bg-slate-900/80" : "bg-slate-50 border border-slate-200"} backdrop-blur shadow-xl`}>
      <div>
        <div className="mb-4 text-sky-400 text-2xl">{icon}</div>
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <ul className={skillText}>
          {items.map((i) => <li key={i} className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-sky-400" /> {i}</li>)}
        </ul>
      </div>
      <div className="mt-4 pt-4 border-t border-slate-700/50">
        <div className="flex justify-between items-center mb-2">
          <span className="text-xs font-semibold text-sky-400">Proficiency</span>
          <span className="text-xs font-bold text-sky-400">{proficiency}%</span>
        </div>
        <motion.div className={`h-2 rounded-full ${dark ? "bg-slate-800" : "bg-slate-200"} overflow-hidden`}>
          <motion.div 
            initial={{ width: 0 }}
            whileInView={{ width: `${proficiency}%` }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-sky-400 to-blue-500"
          />
        </motion.div>
      </div>
    </motion.div>
  );
}

function Project({ title, desc, tech, dark, icon }) {
  const projectCard = dark ? "bg-slate-900 text-slate-100" : "bg-white text-slate-800 border border-slate-200";
  const techText = dark ? "text-sky-300" : "text-sky-600";
  return (
    <motion.div whileHover={{ scale: 1.05, boxShadow: dark ? "0 20px 50px rgba(14, 165, 233, 0.2)" : "0 20px 50px rgba(14, 165, 233, 0.1)" }} className={`p-6 rounded-2xl shadow-xl backdrop-blur min-h-64 flex flex-col justify-between ${projectCard} relative overflow-hidden group`}>
      <div className="absolute top-0 right-0 text-6xl opacity-10 group-hover:opacity-20 transition">{icon}</div>
      <div>
        <h3 className="text-xl font-semibold mb-2 flex items-center gap-2"><span className="text-2xl">{icon}</span> {title}</h3>
        <p className={dark ? "text-slate-400" : "text-slate-600"}>{desc}</p>
      </div>
      <div className="mt-4 flex flex-wrap gap-2">
        {tech.map((t) => (
          <motion.span key={t} whileHover={{ scale: 1.1 }} className={`text-xs px-3 py-1 rounded-full ${dark ? "bg-sky-900/30" : "bg-sky-100"} ${techText}`}>
            {t}
          </motion.span>
        ))}
      </div>
    </motion.div>
  );
}

function Experience({ company, role, period, points, dark, icon, index }) {
  const expText = dark ? "text-slate-400" : "text-slate-600";
  return (
    <motion.div 
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6 }}
      className={`mb-10 p-6 rounded-2xl ${dark ? "bg-slate-900/50 border border-slate-700/50" : "bg-sky-50/50 border border-sky-200/50"}`}
    >
      <div className="flex items-start gap-4">
        <motion.div 
          className="text-3xl min-w-fit"
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {icon}
        </motion.div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold">{role} — {company}</h3>
          <p className={`${expText} mb-3`}>{period}</p>
          <ul className={`space-y-2 ${dark ? "text-slate-300" : "text-slate-700"}`}>
            {points.map((p, idx) => (
              <motion.li key={p} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.1 }} className="flex items-start gap-3">
                <span className="text-sky-400 mt-1">▸</span>
                <span>{p}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
}

function Education({ university, degree, field, period, dark, icon, index }) {
  const eduText = dark ? "text-slate-400" : "text-slate-600";
  return (
    <motion.div 
      variants={itemVariants}
      initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6 }}
      className={`mb-8 p-6 rounded-2xl ${dark ? "bg-slate-900/50 border border-slate-700/50" : "bg-sky-50/50 border border-sky-200/50"}`}
    >
      <div className="flex items-start gap-4">
        <motion.div 
          className="text-3xl min-w-fit"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {icon}
        </motion.div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold">{university}</h3>
          <p className={`font-medium ${dark ? "text-sky-400" : "text-sky-600"} mb-1`}>{degree}</p>
          <p className={`mb-2 ${eduText}`}>{field}</p>
          <p className={`text-sm ${eduText}`}>{period}</p>
        </div>
      </div>
    </motion.div>
  );
}

function CertificationCard({ title, icon, dark }) {
  return (
    <motion.div 
      whileHover={{ y: -8, scale: 1.05 }}
      className={`p-8 rounded-2xl shadow-xl backdrop-blur min-h-64 flex flex-col justify-between ${dark ? "bg-gradient-to-br from-slate-900 to-slate-800 border border-sky-500/20" : "bg-gradient-to-br from-white to-sky-50 border border-sky-300"} relative overflow-hidden group`}
    >
      <motion.div 
        className="absolute top-0 right-0 text-6xl opacity-10 group-hover:opacity-20 transition"
        animate={{ rotate: [0, 5, -5, 0] }}
        transition={{ duration: 3, repeat: Infinity }}
      >
        {icon}
      </motion.div>
      <div className="flex items-start gap-4 relative z-10">
        <motion.div 
          className="text-4xl min-w-fit"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {icon}
        </motion.div>
        <div className="flex-1">
          <motion.div 
            className="flex items-center gap-2 mb-3"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
          >
            <Award className="w-5 h-5 text-sky-400" />
            <span className="text-sm font-semibold text-sky-400">CERTIFIED</span>
          </motion.div>
          <h3 className={`text-lg font-bold leading-snug ${dark ? "text-slate-100" : "text-slate-900"}`}>{title}</h3>
        </div>
      </div>
      <motion.div 
        className={`absolute bottom-0 left-0 h-1 ${dark ? "bg-gradient-to-r from-sky-500 to-blue-500" : "bg-gradient-to-r from-sky-400 to-blue-400"}`}
        initial={{ width: 0 }}
        whileInView={{ width: "100%" }}
        transition={{ duration: 1 }}
      />
    </motion.div>
  );
}
